import PageDataContext from "@core-ui/ContextServices/PageDataContext";
import OPEN_API_NAME from "@ext/markdown/elements/openApi/name";
import type { Selection } from "@tiptap/pm/state";
import { CellSelection } from "prosemirror-tables";
import { useContext } from "react";
import { ActionContext } from "../ButtonStateService";
import type { Attrs, ButtonState, Mark, NodeType, NodeValues } from "./types";

export const ListGroup = ["orderedList", "bulletList", "taskList"];
export const BlockPlus = ["table", "cut", "tab", "tabs", "blockquote"];

const BlockOutContent = ["drawio", "diagrams", "image", "video", "code_block", "fragment", OPEN_API_NAME];
export const WrappableBlocks = ["note"];

const disabledMarkRule: Record<Mark, Mark[]> = {
	code: ["link", "file", "comment", "fragment-link"],
	comment: ["comment", "code", "fragment-link"],
	file: ["link", "file", "comment", "code", "fragment-link"],
	link: ["file", "comment", "code", "fragment-link"],
	strong: ["code"],
	em: ["code"],
	s: [],
	highlight: ["code"],
	"fragment-link": ["link", "file", "comment", "code"],
};

const disableBlockBySelection = {
	table: (selection, buttonNode) => selection instanceof CellSelection && ListGroup.includes(buttonNode),
	noteTitle: () => true,
};

const disabledMarkByAction = {
	noteTitle: () => true,
	code_block: () => true,
};

function changeResultByAction(activeNode: NodeType, buttonNode: NodeType, result: ButtonState) {
	if (!result.isActive) {
		result.isActive = activeNode === buttonNode;
	}

	if (result.disabled) return;

	if (BlockOutContent.includes(activeNode)) {
		if (activeNode === "code_block" && activeNode === buttonNode) {
			result.disabled = false;
		}
	} else if (activeNode === "heading") {
		result.disabled = WrappableBlocks.includes(buttonNode);
	} else if (BlockPlus.includes(activeNode)) {
		result.disabled = activeNode === buttonNode || buttonNode === "heading";
	} else if (WrappableBlocks.includes(activeNode)) {
		result.disabled = buttonNode === "heading";
	}
}

function changeResultByAttrs(contextAttrs: Partial<Attrs>, buttonAttrs: Partial<Attrs>, result: ButtonState) {
	if (!buttonAttrs) return;
	const keys = Object.keys(buttonAttrs);

	keys.forEach((key) => {
		const value = buttonAttrs[key];
		if (contextAttrs?.[key] && value && contextAttrs?.[key] !== value) {
			result.isActive = false;
		}
	});
}

function changeResultByMark(activeNode: NodeType, buttonMark, result: ButtonState) {
	if (!result.disabled && buttonMark) {
		result.disabled = disabledMarkByAction[activeNode]?.();
	}
}

function changeResultBySelection(
	activeNode: NodeType,
	selection: Selection,
	buttonNode: NodeType,
	result: ButtonState,
) {
	if (!(activeNode in disableBlockBySelection)) return;

	if (disableBlockBySelection[activeNode]?.(selection, buttonNode)) {
		result.disabled = true;
	}
}

function changeResultByWrappableBlocks(activeNode: NodeType, buttonNode: NodeType, result: ButtonState) {
	if (WrappableBlocks.includes(activeNode) && buttonNode === activeNode) {
		result.disabled = activeNode !== buttonNode;
	}
}

function getButtonStateByMarks(contextMarks: Mark[], buttonMark: Mark, result: ButtonState) {
	if (!contextMarks?.length) return;

	if (buttonMark) {
		if (!result.isActive) {
			result.isActive = contextMarks.includes(buttonMark);
		}
		if (!result.disabled) {
			result.disabled = disabledMarkRule[buttonMark]?.some((mark) => contextMarks.includes(mark)) || false;
		}
	}
}

interface ResultByActionDataProps {
	actions?: NodeType[];
	attrs?: Partial<Attrs>;
	marks?: Mark[];
	currentNode: NodeValues;
	selection: Selection;
}

export const getResultByActionData = (props: ResultByActionDataProps) => {
	const { currentNode: current, attrs, marks, actions, selection } = props;
	const result = { isActive: false, disabled: false, attrs, marks };

	actions.forEach((action) => {
		changeResultByAction(action, current.action, result);
		changeResultByMark(action, current.mark, result);
		changeResultByAttrs(attrs, current.attrs, result);
		changeResultBySelection(action, selection, current.action, result);
		getButtonStateByMarks(marks, current.mark, result);
		changeResultByWrappableBlocks(action, current.action, result);
	});

	return result;
};

const useCurrentAction = (currentNode: NodeValues) => {
	const { actions, attrs, marks, selection } = useContext(ActionContext);
	const isReadOnly = PageDataContext.value?.conf?.isReadOnly;

	const result = getResultByActionData({ actions, attrs, marks, currentNode, selection });
	if (isReadOnly) result.disabled = true;
	return result;
};

export default useCurrentAction;
